import React, { useEffect, useState } from 'react';
import CashierDataApi from "../../services/cashier/fetchCashiers.js";
import { SquarePen, Trash, Search, Plus, ListFilter } from 'lucide-react';
import { Button, InputGroup, FormControl } from 'react-bootstrap';
import AddCashierModal from '../commonComponents/CashierControlModals/AddCashierModal.jsx'
import DeleteCashierModal from '../commonComponents/CashierControlModals/DeleteCashierModal.jsx';
import Form from 'react-bootstrap/Form';
import EditCashierModal from '../commonComponents/CashierControlModals/EditCashierModal.jsx';
import style from "./Cashiers.module.scss"
import { toast } from 'react-toastify';
import Dropdown from 'react-bootstrap/Dropdown';
import DeleteCashier from '../../services/cashier/deleteCashier.js';
import CASHIER_TOASTS from '../../utils/constants/toastMesages/cashierToastMessage.js';
import { APP_CONFIG } from '../../utils/runtimeConfig.js';
/**
 * Cashiers component displays the list of cashiers with add and delete functionality.
 *
 * @component
 * @returns {JSX.Element}
 */
function Cashiers() {
  // State to store the list of cashiers
  const [cashiers, setCashiers] = useState([]);

  // Controls Add Cashier Modal visibility
  const [modalShow, setModalShow] = useState(false);

  // Controls Delete Cashier Modal visibility
  const [deleteModalShow, setDeleteModalShow] = useState(false);

  // Stores the cashier object to delete
  const [deleteCashier, setDeleteCashier] = useState(null);

  // Stores the Cashier object to edit
  const [editCashier, setEditCashier] = useState(null);

  //States for handling edit cashier modal show
  const [editModalShow, setEditModalShow] = useState(false)

  //States for tracking search query
  const [searchQuery, setSearchQuery] = useState("")

  //States for saving and selecting all the cashier select box
  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  //States for saving states for tracking active or inactive cashiers 
  const [activeCashiers, setActiveCashiers] = useState("all")

  //State for fetching/updating table after cashier data is been deleted
  const [refetchFlag, setRefetchFlag] = useState(false);

  // For tracking pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10; // You can adjust this as needed


  //Getting user roles from environment
  const userRole = APP_CONFIG.roles;

  /**
   * Opens delete modal and sets the cashier to be deleted
   * @param {Object} cashier - Cashier object to delete
   */
  const deleteCashierHandler = (cashier) => {
    setDeleteCashier(cashier);
    setDeleteModalShow(true);
  };

  /**
 * Opens edit modal and sets the cashier to be deleted
 * @param {Object} cashier - Cashier object to delete
 */
  const editCashierHandler = (cashier) => {
    setEditCashier(cashier);
    setEditModalShow(true);
  };

  /**
   * Fetches cashier data from API
   */
  useEffect(() => {
    const fetchCashierData = async () => {
      try {
        const res = await CashierDataApi();
        setCashiers(res.data);
      } catch (error) {
        // Consider adding user feedback here if fetch fails
        console.error(CASHIER_TOASTS.CASHIER_NOT_FETCHED);
      }
    };
    fetchCashierData();
  }, [modalShow, deleteModalShow, editModalShow, refetchFlag]);

  //Filtered list
  // const filteredCashiers = cashiers
  // .filter(person => person.role === userRole.CASHIER)
  // .filter(person =>
  //   person.name.toLowerCase().includes(searchQuery.toLowerCase()) || person.username.toLowerCase().includes(searchQuery.toLowerCase())
  // );
  const filteredCashiers = (cashiers || [])
    .filter(person => person?.role === userRole.CASHIER)
    .filter(person => {
      const name = (person.name || '').toLowerCase();
      const username = (person.username || '').toLowerCase();
      const query = (searchQuery || '').toLowerCase();
      return name.includes(query) || username.includes(query);
    });

  // Pagination logic
  const visibleCashiers = filteredCashiers
    .filter(person => person.role === userRole.CASHIER)
    .filter(person => {
      if (activeCashiers === "active") return person.active === true;
      if (activeCashiers === "inactive") return person.active === false;
      return true;
    });

  const totalPages = Math.ceil(visibleCashiers.length / itemsPerPage);
  //Function for selecting all checbox by th select box
  const selectAllcheckBox = () => {
    const allIds = filteredCashiers.filter(person => person.role === userRole.CASHIER).map(person => person._id);
    if (selectAll) {
      setSelectedIds([]);
    }
    else {
      setSelectedIds(allIds);
    }
    setSelectAll(!selectAll);
  }

  //Function for handling single checkbox selection
  const handleSingleSelect = (id) => {
    if (selectedIds.includes(id)) {
      setSelectedIds(prev => prev.filter(_id => _id !== id));
    }
    else {
      setSelectedIds(prev => [...prev, id])
    }
  }

  //Delete selected cashier 
  const deleteMultipleCashier = async () => {
    try {
      for (const item of selectedIds) {
        await DeleteCashier(item);
        setSelectedIds([]);

      }
      toast.success();
      setRefetchFlag(prev => !prev);
    }
    catch (error) {
      toast.error(CASHIER_TOASTS.CASHIERS_NOT_DELETED);
    }
  }

  return (
    <div className={`${style.containerDiv} w-100`}>
      {/* Header to mirror Products table UI */}
      <div className={`${style.header} d-flex justify-content-between align-items-center mb-4`}>
        <div>
          <h4 className="mb-0">Cashiers Management</h4>
          <p className="text-muted mb-0">
            Manage restaurant cashiers and their access
          </p>
        </div>
      </div>

      <div className={`${style.SearchButton} d-flex flex-column flex-md-row mb-3 justify-content-between`}>
        {/* Search Input */}
        <InputGroup className={`${style.searchBox}`}>
          <InputGroup.Text><Search size={16} /></InputGroup.Text>
          <FormControl
            placeholder="Search cashier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </InputGroup>

        <div className='gap-2 d-flex align-items-center'>

          <Dropdown>
            <Dropdown.Toggle variant="success" id="dropdown-basic">
              <ListFilter width={40} />
            </Dropdown.Toggle>

            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setActiveCashiers("all")}>All Cashiers</Dropdown.Item>
              <Dropdown.Item onClick={() => setActiveCashiers("active")}>Active Cashiers</Dropdown.Item>
              <Dropdown.Item onClick={() => setActiveCashiers("inactive")}>Inactive Cashiers</Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
          <div className='d-flex gap-3 w-100'>
            <Button
              className={`${style.addCashierBtn} btn-sm d-flex align-items-center justify-content-center gap-2`}
              onClick={() => setModalShow(true)}
              variant="primary"
            >
              <Plus size={15} />Add Cashier
            </Button>
            {
              selectedIds.length > 0 && <div className={`pt-1 ${style.selectedTrash}`} onClick={deleteMultipleCashier}><Trash /></div>
            }
          </div>
        </div>
      </div>

      <div className={`${style.tableWrapper}`}>
        <div className="table-responsive">
          <table className={`table ${style.categoriesTable}`}>
            <thead>
              <tr>
                <th>
                  <Form.Check // prettier-ignore
                    type="checkbox"
                    onClick={() => selectAllcheckBox()}
                    id={`default-checkbox`} />
                </th>
                <th className={`${style.tableCell} text-center`}>Name</th>
                <th className={`${style.tableCell} text-center`}>User Name</th>
                <th className={`${style.tableCell} text-center`}>Email</th>
                <th className={`${style.tableCell} text-center`}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredCashiers.filter(person => person.role === userRole.CASHIER).filter(person => {
                if (activeCashiers === "active") return person.active === true;
                if (activeCashiers === "inactive") return person.active === false;
                return true;
              }).map(person => (
                <tr key={person._id} style={{ cursor: "pointer" }}>
                  <td>
                    <Form.Check // prettier-ignore
                      checked={selectedIds.includes(person._id)}
                      type="checkbox"
                      onChange={() => handleSingleSelect(person._id)}
                      id={`default-checkbox`} />
                  </td>
                  <td className={`${style.tableCell} text-center`}>{person.name}</td>
                  <td className={`${style.tableCell} text-center`}>{person.username}</td>
                  <td className={`${style.tableCell} text-center`}>{person.email}</td>
                  <td className="text-center d-flex gap-3 justify-content-center">
                    {/* Edit button (currently no handler) */}
                    <div
                      className={`${style.editButton} bg-warning`}
                      onClick={(e) => { e.stopPropagation(); editCashierHandler(person) }}
                    >
                      <SquarePen height={17} />
                    </div>

                    {/* Delete button */}
                    <div
                      className={`${style.deleteButton} bg-danger`}
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteCashierHandler(person);
                      }}
                    >
                      <Trash height={17} color="white" />
                    </div>
                  </td>
                </tr>
              ))}

              {visibleCashiers.length === 0 && (
                <tr>
                  <td className={`${style.tableCell} text-center`} colSpan="5">
                    No cashiers found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-3 mt-3">
          <Button
            variant="outline-secondary"
            size="sm"
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
          >
            &laquo;
          </Button>

          <span>
            Page <strong>{currentPage}</strong> of {totalPages}
          </span>

          <Button
            variant="outline-secondary"
            size="sm"
            disabled={currentPage === totalPages}
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
          >
            &raquo;
          </Button>
        </div>
      )}

      {/* Add Cashier Modal */}
      <AddCashierModal show={modalShow} onHide={() => setModalShow(false)} />

      {/* Delete Cashier Modal */}
      <DeleteCashierModal
        show={deleteModalShow}
        onHide={() => setDeleteModalShow(false)}
        cashier={deleteCashier}
      />

      {/* Edit Cashier Modal */}
      <EditCashierModal
        show={editModalShow}
        onHide={() => setEditModalShow(false)}
        cashier={editCashier}
      />
    </div>
  );
}

export default Cashiers;
